import { useMutation } from "@apollo/client/react";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { toast } from "sonner";
import {
  GetRecurringTransactionsDocument,
  CreateRecurringTransactionDocument,
  UpdateRecurringTransactionDocument,
  DeleteRecurringTransactionDocument,
  type CreateRecurringTransactionMutation,
  type UpdateRecurringTransactionMutation,
  type DeleteRecurringTransactionMutation,
} from "@/src/gql/graphql";
import type { RecurringTransaction } from "@/lib/types/recurring-transactions";
import { QB_REALM } from "@/lib/integrations/quickbooks/realms";
import { validateCadenceAnchorInput } from "@/lib/recurring-transactions/billing-cadence";

const ANCHOR_CHECK_MESSAGE = "Quarterly and annual billing need a next bill date.";

/** Friendly text for the DB CHECK that backstops the cross-field rule. */
function isAnchorCheckViolation(err: unknown): boolean {
  return err instanceof Error && err.message.includes("recurring_transactions_next_bill_on_required_check");
}

export function useCreateRecurringTransaction() {
  const queryClient = useQueryClient();

  const [mutate, { loading, error }] = useMutation<CreateRecurringTransactionMutation>(
    CreateRecurringTransactionDocument
  );

  const createTransaction = useCallback(
    async (input: {
      customer_id: string;
      item: string;
      description?: string;
      amount: number;
      quantity?: number;
      quickbooks_realm_id?: string;
      billing_cadence?: string;
      next_bill_on?: string | null;
    }) => {
      const cadenceError = validateCadenceAnchorInput(input.billing_cadence, input.next_bill_on);
      if (cadenceError) {
        toast.error(cadenceError);
        throw new Error(cadenceError);
      }
      try {
        const { amount, quantity, quickbooks_realm_id, next_bill_on, billing_cadence, ...rest } = input;
        const quantityValue =
          quantity !== undefined && quantity !== null ? String(quantity) : "1";

        const result = await mutate({
          variables: {
            input: {
              ...rest,
              amount: String(amount),
              quantity: quantityValue,
              // Monthly is the column default: send nothing so a monthly create is
              // the exact insert it always was.
              ...(billing_cadence && billing_cadence !== "monthly"
                ? { billing_cadence, next_bill_on }
                : {}),
              // Default new lines to the West Tennessee Consulting realm (all
              // existing rows are WTC); the create form can override to Schooley
              // Mitchell.
              quickbooks_realm_id:
                quickbooks_realm_id || QB_REALM.WEST_TENNESSEE_CONSULTING,
            },
          },
          refetchQueries: [{ query: GetRecurringTransactionsDocument }],
        });

        const record = result.data?.insertIntorecurring_transactionsCollection?.records?.[0];
        if (record) {
          void queryClient.invalidateQueries({ queryKey: ["recurring-transactions"] });
          toast.success("Recurring transaction created successfully");
        }
        return record;
      } catch (err) {
        toast.error("Failed to create recurring transaction");
        throw err;
      }
    },
    [mutate, queryClient]
  );

  return { createTransaction, loading, error };
}

export function useUpdateRecurringTransaction() {
  const queryClient = useQueryClient();

  const [mutate, { loading, error }] = useMutation<UpdateRecurringTransactionMutation>(
    UpdateRecurringTransactionDocument
  );

  const updateTransaction = useCallback(
    async (id: string, input: Partial<RecurringTransaction>) => {
      const cadenceError = validateCadenceAnchorInput(input.billing_cadence, input.next_bill_on);
      if (cadenceError) {
        toast.error(cadenceError);
        throw new Error(cadenceError);
      }
      try {
        const result = await mutate({
          variables: {
            id,
            input: {
              ...(input.customer_id !== undefined && { customer_id: input.customer_id }),
              ...(input.item !== undefined && { item: input.item }),
              ...(input.description !== undefined && { description: input.description }),
              ...(input.amount !== undefined && { amount: String(input.amount) }),
              ...(input.quantity !== undefined && { quantity: String(input.quantity) }),
              ...(input.billing_cadence !== undefined && { billing_cadence: input.billing_cadence }),
              // Moving a line back to monthly drops its anchor (DB CHECK parity).
              ...(input.billing_cadence === "monthly"
                ? { next_bill_on: null }
                : input.next_bill_on !== undefined && { next_bill_on: input.next_bill_on }),
              ...(input.sync_status !== undefined && { sync_status: input.sync_status }),
              ...(input.last_synced_at !== undefined && { last_synced_at: input.last_synced_at }),
              ...(input.last_synced_month !== undefined && {
                last_synced_month: input.last_synced_month,
              }),
              ...(input.quickbooks_id !== undefined && { quickbooks_id: input.quickbooks_id }),
              ...(input.quickbooks_realm_id !== undefined && {
                quickbooks_realm_id: input.quickbooks_realm_id,
              }),
            },
          },
          refetchQueries: [{ query: GetRecurringTransactionsDocument }],
        });

        const record = result.data?.updaterecurring_transactionsCollection?.records?.[0];
        if (record) {
          void queryClient.invalidateQueries({ queryKey: ["recurring-transactions"] });
          toast.success("Recurring transaction updated successfully");
        }
        return record;
      } catch (err) {
        toast.error(
          isAnchorCheckViolation(err) ? ANCHOR_CHECK_MESSAGE : "Failed to update recurring transaction"
        );
        throw err;
      }
    },
    [mutate, queryClient]
  );

  return { updateTransaction, loading, error };
}

export function useDeleteRecurringTransaction() {
  const queryClient = useQueryClient();

  const [mutate, { loading, error }] = useMutation<DeleteRecurringTransactionMutation>(
    DeleteRecurringTransactionDocument
  );

  const deleteTransaction = useCallback(
    async (id: string) => {
      try {
        const result = await mutate({
          variables: { id },
          refetchQueries: [{ query: GetRecurringTransactionsDocument }],
        });

        const record = result.data?.deleteFromrecurring_transactionsCollection?.records?.[0];
        if (record) {
          void queryClient.invalidateQueries({ queryKey: ["recurring-transactions"] });
          toast.success("Recurring transaction deleted successfully");
        }
        return record;
      } catch (err) {
        toast.error("Failed to delete recurring transaction");
        throw err;
      }
    },
    [mutate, queryClient]
  );

  return { deleteTransaction, loading, error };
}
